/**
 * SystemHealth — coach/owner-facing view of the nightly integrity sweep.
 *
 * Lists open anomalies grouped by kind, severity badge per row, a "Run
 * sweep now" button to refresh on demand, and a "Resolve" button per
 * row. Resolving is a soft-acknowledge — the next sweep reopens the
 * alert if the underlying data condition is still present.
 *
 * Companion to /backend/routes/system_health.py. Owner spec 2026-02-28:
 * "make sure at night, there's a sweep" + morning digest. This is the
 * page the digest email links to.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    AlertOctagon, AlertTriangle, Info, RefreshCcw, CheckCircle2,
    Loader2, Mail, ShieldCheck, ExternalLink, Search,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_META = {
    critical: { icon: AlertOctagon, color: "text-brand-red", border: "border-brand-red/60", bg: "bg-brand-red/10", label: "Critical" },
    warning: { icon: AlertTriangle, color: "text-amber-400", border: "border-amber-700/60", bg: "bg-amber-950/30", label: "Warning" },
    info: { icon: Info, color: "text-sky-300", border: "border-sky-800/60", bg: "bg-sky-950/30", label: "Info" },
};

const KIND_LABELS = {
    kid_missing_perms: "Kid missing perms",
    orphan_athlete: "Orphan athlete",
    phantom_member: "Phantom member",
    stale_active_sub: "Stale active sub",
    orphan_booking: "Orphan booking",
    stale_waiver: "Stale waiver",
};

function fmtTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function SystemHealth() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [filter, setFilter] = useState("open");
    const [busyKey, setBusyKey] = useState(null);

    const load = async (status = filter) => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/system-health/alerts?status=${status}`, { withCredentials: true });
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load alerts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

    const runSweep = async () => {
        setRunning(true);
        try {
            const r = await axios.post(`${API}/coach/system-health/run-sweep-now`, {}, { withCredentials: true });
            toast.success("Sweep complete", {
                description: `${r.data.total_open} open · ${r.data.auto_resolved} auto-resolved · ${r.data.elapsed_ms}ms`,
            });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sweep failed");
        } finally {
            setRunning(false);
        }
    };

    const sendDigest = async () => {
        try {
            const r = await axios.post(`${API}/coach/system-health/send-digest-now`, {}, { withCredentials: true });
            if (r.data?.sent) {
                toast.success("Digest sent", { description: `To ${r.data.to.join(", ")}` });
            } else {
                toast.info(`Digest skipped — ${r.data.reason}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send digest");
        }
    };

    const resolve = async (alertKey) => {
        setBusyKey(alertKey);
        try {
            await axios.post(`${API}/coach/system-health/alerts/${encodeURIComponent(alertKey)}/resolve`, {}, { withCredentials: true });
            toast.success("Resolved", { description: "Next sweep will reopen it if the condition persists." });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't resolve");
        } finally {
            setBusyKey(null);
        }
    };

    if (loading || !data) {
        return (
            <div data-testid="system-health-loading" className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-brand-red" />
            </div>
        );
    }

    const lastRun = data.last_run;
    const lastResult = data.last_result || {};

    return (
        <div data-testid="system-health-page" className="max-w-6xl mx-auto px-6 py-10 text-white">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-2">
                        Operations · Admin
                    </div>
                    <h1 className="font-display text-4xl tracking-wider">System health</h1>
                    <p className="mt-2 text-sm text-zinc-300 max-w-xl">
                        Nightly integrity sweep results. Data anomalies are surfaced here so they can be triaged before
                        a user hits them. Resolving is a soft-ack — the next sweep reopens any alert whose condition
                        still fires.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        data-testid="run-sweep-btn"
                        onClick={runSweep}
                        disabled={running}
                        className="btn-primary-tactical text-xs disabled:opacity-50"
                    >
                        <RefreshCcw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} /> Run sweep now
                    </button>
                    <button
                        type="button"
                        data-testid="send-digest-btn"
                        onClick={sendDigest}
                        className="btn-ghost-tactical text-xs"
                    >
                        <Mail className="w-3.5 h-3.5" /> Send digest now
                    </button>
                </div>
            </div>

            {/* Severity summary tiles */}
            <div className="grid grid-cols-3 gap-3 mb-8">
                {(["critical", "warning", "info"]).map((sev) => {
                    const meta = SEVERITY_META[sev];
                    const Icon = meta.icon;
                    return (
                        <div
                            key={sev}
                            data-testid={`severity-tile-${sev}`}
                            className={`rounded-2xl border ${meta.border} ${meta.bg} px-4 py-4`}
                        >
                            <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-zinc-300">
                                <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                                {meta.label}
                            </div>
                            <div className="font-display text-4xl tracking-wider text-white mt-1">
                                {data.by_severity?.[sev] ?? 0}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Last run meta */}
            <div className="text-xs text-zinc-400 mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                    Last sweep: <span className="text-zinc-200">{fmtTime(lastRun)}</span>
                </span>
                {lastResult.elapsed_ms != null && <span>· {lastResult.elapsed_ms}ms</span>}
                {lastResult.auto_resolved > 0 && <span>· {lastResult.auto_resolved} auto-resolved</span>}
                <a
                    href="/coach/unsettled-ious"
                    data-testid="link-unsettled-ious"
                    className="ml-auto text-brand-red hover:underline text-[10px] tracking-[0.2em] uppercase"
                >
                    Unsettled IOUs →
                </a>
            </div>

            {/* ----- Workout-access diagnostic (Sachin 2026-02-28) ----- */}
            <WorkoutAccessDiagnostic />

            {/* Filter */}
            <div className="flex items-center gap-1 mb-4">
                {["open", "resolved", "all"].map((f) => (
                    <button
                        key={f}
                        type="button"
                        data-testid={`filter-${f}`}
                        onClick={() => setFilter(f)}
                        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                            filter === f
                                ? "bg-brand-red border-brand-red text-white"
                                : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                        }`}
                    >
                        {f === "open" ? "Open" : f === "resolved" ? "Resolved" : "All"}
                    </button>
                ))}
            </div>

            {/* Alerts list */}
            {data.alerts.length === 0 ? (
                <div
                    data-testid="alerts-empty"
                    className="border border-emerald-700/60 bg-emerald-950/30 rounded-2xl px-6 py-10 text-center"
                >
                    <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                    <div className="font-display text-xl tracking-wider text-white">All clear</div>
                    <div className="text-xs text-zinc-300 mt-1">
                        No {filter === "open" ? "open" : filter} alerts in the system right now.
                    </div>
                </div>
            ) : (
                <div className="border border-zinc-800 rounded-2xl divide-y divide-zinc-900 bg-zinc-950/40">
                    {data.alerts.map((a) => {
                        const meta = SEVERITY_META[a.severity] || SEVERITY_META.info;
                        const Icon = meta.icon;
                        return (
                            <div
                                key={a.alert_key}
                                data-testid={`alert-row-${a.alert_key}`}
                                className="px-4 py-3 flex items-start gap-3 flex-wrap"
                            >
                                <Icon className={`w-4 h-4 mt-1 ${meta.color} shrink-0`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-0.5">
                                        <span className="text-zinc-200">{KIND_LABELS[a.kind] || a.kind}</span>
                                        <span>·</span>
                                        <span>first seen {fmtTime(a.first_seen_at)}</span>
                                    </div>
                                    <div className="text-sm text-zinc-100">{a.summary}</div>
                                    <div className="mt-1 text-[11px] text-zinc-500 truncate">
                                        <span className="font-mono">{a.entity_type}:{a.entity_id}</span>
                                    </div>
                                    {a.resolved_at && (
                                        <div className="mt-1 text-[11px] text-emerald-400">
                                            Resolved {fmtTime(a.resolved_at)} by {a.resolved_by === "auto_sweep" ? "auto-sweep" : "coach"}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-1 items-end shrink-0">
                                    {a.entity_type === "user" && (
                                        <a
                                            href={`/coach/leads/${a.entity_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[11px] text-zinc-300 hover:text-white inline-flex items-center gap-1"
                                        >
                                            Open profile <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                    {!a.resolved_at && (
                                        <button
                                            type="button"
                                            data-testid={`resolve-${a.alert_key}`}
                                            onClick={() => resolve(a.alert_key)}
                                            disabled={busyKey === a.alert_key}
                                            className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-200 hover:border-emerald-700 hover:text-emerald-300 disabled:opacity-50"
                                        >
                                            {busyKey === a.alert_key ? "…" : "Resolve"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}



/**
 * WorkoutAccessDiagnostic — single-email lookup that traces every gate
 * deciding whether a kid's "Workouts" button is greyed out: comp_access,
 * dunning hold, legacy_blocked sub, missing kid user, wrong parent link.
 *
 * Sachin 2026-02-28: built after a recurring "X is still greyed out"
 * loop. Lives on /coach/system-health so owner can paste any email in
 * 5 seconds during a support thread.
 */
function WorkoutAccessDiagnostic() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);

    const run = async (e) => {
        e?.preventDefault();
        const q = email.trim();
        if (!q) return;
        setBusy(true);
        setErr(null);
        setResult(null);
        try {
            const r = await axios.get(
                `${API}/coach/diagnostics/workout-access?email=${encodeURIComponent(q)}`,
                { withCredentials: true },
            );
            setResult(r.data);
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Lookup failed");
        } finally {
            setBusy(false);
        }
    };

    const gateColor = {
        comp_access: "text-emerald-300 border-emerald-700/60 bg-emerald-950/30",
        active_subscription: "text-emerald-300 border-emerald-700/60 bg-emerald-950/30",
        dunning_access_suspended: "text-amber-300 border-amber-700/60 bg-amber-950/30",
        legacy_blocked: "text-amber-300 border-amber-700/60 bg-amber-950/30",
        past_due: "text-amber-300 border-amber-700/60 bg-amber-950/30",
        cancelled: "text-zinc-300 border-zinc-700 bg-zinc-900/60",
        incomplete: "text-amber-300 border-amber-700/60 bg-amber-950/30",
        no_paying_record: "text-brand-red border-brand-red/60 bg-brand-red/10",
        no_account: "text-brand-red border-brand-red/60 bg-brand-red/10",
    }[result?.gate || result?.verdict] || "text-zinc-300 border-zinc-700 bg-zinc-900/60";

    return (
        <div
            data-testid="workout-access-diagnostic"
            className="mb-8 border border-zinc-800 rounded-2xl bg-zinc-950/60 p-5"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-1">Diagnose access</div>
                    <h2 className="font-display text-lg tracking-wider">Why is this email&apos;s workouts greyed out?</h2>
                    <p className="text-xs text-zinc-400 mt-1 max-w-xl">
                        Paste any parent OR kid email. Returns the gate that&apos;s blocking access — comp / sub / dunning /
                        legacy_blocked / missing kid / wrong parent link — in one shot.
                    </p>
                </div>
            </div>

            <form onSubmit={run} className="flex gap-2 mb-4">
                <input
                    data-testid="diagnostic-email-input"
                    type="email"
                    placeholder="parent@example.com or kid@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-black border border-zinc-700 rounded-xl px-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-brand-red focus:outline-none"
                />
                <button
                    type="submit"
                    data-testid="diagnostic-run-btn"
                    disabled={busy || !email.trim()}
                    className="btn-primary-tactical text-xs disabled:opacity-50"
                >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                    Diagnose
                </button>
            </form>

            {err && (
                <div data-testid="diagnostic-error" className="text-sm text-brand-red border border-brand-red/40 rounded-xl px-3 py-2 bg-brand-red/10">
                    {err}
                </div>
            )}

            {result && (
                <div className="space-y-3">
                    <div
                        data-testid="diagnostic-verdict"
                        className={`border rounded-xl px-4 py-3 ${gateColor}`}
                    >
                        <div className="text-[10px] tracking-[0.25em] uppercase mb-1 opacity-80">
                            {result.verdict ? "Verdict" : `Gate: ${result.gate}`}
                        </div>
                        <div className="text-sm leading-relaxed">{result.verdict_explainer}</div>
                    </div>

                    {result.user && (
                        <div className="grid sm:grid-cols-2 gap-3 text-xs">
                            <div className="border border-zinc-800 rounded-xl p-3 bg-black/40">
                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Matched user ({result.matched_as})</div>
                                <KV k="user_id" v={result.user.user_id} />
                                <KV k="email" v={result.user.email} />
                                <KV k="name" v={result.user.name} />
                                <KV k="role" v={result.user.role} />
                                <KV k="comp_access" v={String(!!result.comp_access)} />
                                <KV k="dunning_suspended" v={String(!!result.dunning_access_suspended)} />
                                <KV k="has_active_subscription" v={String(!!result.has_active_subscription)} />
                            </div>
                            {result.kid && (
                                <div className="border border-zinc-800 rounded-xl p-3 bg-black/40">
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Kid (athlete-mode)</div>
                                    <KV k="user_id" v={result.kid.user_id} />
                                    <KV k="athlete_mode_for" v={result.kid.athlete_mode_for} />
                                    <KV k="sub_override_applied" v={String(!!result.kid.sub_override_applied)} />
                                    <KV k="workouts perm (final)" v={String(!!result.kid.workouts_perm_final)} />
                                </div>
                            )}
                        </div>
                    )}

                    {result.subscriptions && result.subscriptions.length > 0 && (
                        <div className="border border-zinc-800 rounded-xl p-3 bg-black/40">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Subscriptions on paying account</div>
                            {result.subscriptions.map((s) => (
                                <div key={s.subscription_id} className="text-xs py-1.5 border-t border-zinc-900 first:border-0">
                                    <span className={s.unlocks_workouts ? "text-emerald-300" : "text-zinc-400"}>
                                        {s.unlocks_workouts ? "✓" : "✗"}
                                    </span>{" "}
                                    <span className="text-zinc-200">{s.plan_name || "—"}</span>
                                    <span className="text-zinc-500"> · status={s.status}</span>
                                    {s.legacy_blocked && <span className="text-amber-300"> · legacy_blocked</span>}
                                    <div className="text-[11px] text-zinc-500 ml-4">{s.reason}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {result.all_athlete_mode_kids && result.all_athlete_mode_kids.length > 0 && (
                        <div className="border border-zinc-800 rounded-xl p-3 bg-black/40">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Athlete-mode kids linked to this parent</div>
                            {result.all_athlete_mode_kids.map((k) => (
                                <div key={k.user_id} className="text-xs py-1 text-zinc-300">
                                    {k.email} <span className="text-zinc-500">· athlete_id={k.athlete_mode_for}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <details className="text-xs">
                        <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200">Show raw JSON</summary>
                        <pre data-testid="diagnostic-raw-json" className="mt-2 text-[11px] text-zinc-300 bg-black/60 border border-zinc-900 rounded-xl p-3 overflow-auto max-h-80">
{JSON.stringify(result, null, 2)}
                        </pre>
                    </details>
                </div>
            )}
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div className="flex justify-between gap-3 py-0.5">
            <span className="text-zinc-500">{k}</span>
            <span className="text-zinc-200 truncate text-right max-w-[60%]">{v ?? "—"}</span>
        </div>
    );
}
